import { cn, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { LandTypePctDistribution } from '@/api/rest/resources/types/organization';
import { PlotType } from '@/api/rest/resources/types/plot';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartShape, PieChartConfig } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useNaturalCapitalAccount } from '@/hooks/useNaturalCapitalAccount';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';

export const useLandUseDistributionChartConfiguration = () => {
  const data = useNaturalCapitalAccount().data.land_type_percent_distribution;
  const { t } = useTranslation();

  const noChartData = isNullish(data) || data.length === 0;

  const commonClasses = 'border-none typography-h3 px-0';

  const chartConfig: PieChartConfig[] = [
    {
      shape: ChartShape.pie,
      id: 'land_use_distribution',
      key: 'land_use_distribution',
      dataKey: 'value',
      innerRadius: 90,
      outerRadius: 108,
      startAngle: 90,
      endAngle: 450,
      paddingAngle: 0,
      cx: '50%',
      cy: '50%',
    },
  ];

  const dataWithColors = data.map((item) => ({
    ...item,
    color: getColorForLandUse(item.name),
  }));

  const legendItems = data.map((item) => ({
    icon: 'ri-circle-fill',
    className: 'bg-neutral-black-4 p-2 rounded-lg',
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    label: `${t(`global.plotTypes.${item.name}`)} ${item.value}%`,
    color: getColorForLandUse(item.name),
  }));

  return {
    name: 'land_use_distribution_card',
    type: WidgetType.o_card,
    visible: !noChartData,
    relatedData: {
      styles: {
        root: cn('h-full', commonClasses),
        header: commonClasses,
        body: 'h-full flex flex-col justify-center p-2 border border-neutral-black-12 rounded-2xl',
      },
      header: t('global.widget.landDistribution'),
    },
    widgets: [
      {
        name: 'land_use_distribution_graph',
        type: WidgetType.o_composite_graph,
        visible: true,
        relatedData: {
          'data-testid': 'land-use-distribution-chart',
          data: dataWithColors,
          chartConfig,
          legendItems,
          tooltip: CustomTooltip,
        },
      },
    ],
  };
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload: LandTypePctDistribution;
  }>;
};

const getColorForLandUse = (name: string) => {
  return colorPalette[`landtypes-${name}-main`] ?? colorPalette['neutral-black-12'];
};

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className='rounded-md bg-white-100 px-3 py-2 shadow-lg border border-neutral-black-12'>
      <Stack direction='row' centerMain spacing={2} className='mb-1'>
        <div className='w-3 h-3 rounded-full' style={{ backgroundColor: getColorForLandUse(data.name) }} />
        <span className='font-medium text-sm text-gray-900'>{t(`global.plotTypes.${data.name as PlotType}`)}</span>
      </Stack>
      <div className='text-sm text-gray-600'>{getDisplayNumber(data.value, window.navigator.language)}%</div>
    </div>
  );
};
